import { SCRATCH_R_THRESHOLD, STARTING_EQUITY } from './config';
import type { Stats, StatsFilter, SubStats, Trade } from './types';

const isClosed = (t: Trade): boolean => t.resolution !== 'open';

type Class = 'win' | 'loss' | 'scratch' | 'open';

function classify(t: Trade): Class {
  if (t.resolution === 'open' || t.rMultiple === null) return 'open';
  if (Math.abs(t.rMultiple) < SCRATCH_R_THRESHOLD) return 'scratch';
  return t.rMultiple > 0 ? 'win' : 'loss';
}

function applyFilter(trades: Trade[], filter?: StatsFilter): Trade[] {
  if (!filter) return trades;
  return trades.filter((t) => {
    if (filter.setup !== undefined && t.plan.setup !== filter.setup) return false;
    if (filter.direction !== undefined && t.plan.direction !== filter.direction) return false;
    if (filter.yFlipped !== undefined && t.yFlipped !== filter.yFlipped) return false;
    if (filter.from !== undefined && t.enteredAt < filter.from) return false;
    if (filter.to !== undefined && t.enteredAt > filter.to) return false;
    return true;
  });
}

function computeSubStats(trades: Trade[]): SubStats {
  const closed = trades.filter(isClosed);
  let wins = 0;
  let losses = 0;
  let totalPnl = 0;
  let sumR = 0;
  let rCount = 0;
  for (const t of closed) {
    totalPnl += t.pnl ?? 0;
    if (t.rMultiple !== null) {
      sumR += t.rMultiple;
      rCount++;
    }
    const c = classify(t);
    if (c === 'win') wins++;
    else if (c === 'loss') losses++;
  }
  const wlSum = wins + losses;
  return {
    totalTrades: trades.length,
    closedTrades: closed.length,
    wins,
    losses,
    winRate: wlSum > 0 ? wins / wlSum : 0,
    avgR: rCount > 0 ? sumR / rCount : 0,
    totalPnl,
  };
}

function computeMaxDrawdown(closedTrades: Trade[]): number {
  const ordered = [...closedTrades].sort((a, b) => a.enteredAt - b.enteredAt);
  let equity = STARTING_EQUITY;
  let peak = STARTING_EQUITY;
  let maxDD = 0;
  for (const t of ordered) {
    equity += t.pnl ?? 0;
    if (equity > peak) peak = equity;
    const dd = peak > 0 ? (peak - equity) / peak : 0;
    if (dd > maxDD) maxDD = dd;
  }
  return maxDD;
}

export function computeStats(rawTrades: Trade[], filter?: StatsFilter): Stats {
  const trades = applyFilter(rawTrades, filter);
  const closed = trades.filter(isClosed);

  let wins = 0;
  let losses = 0;
  let scratches = 0;
  let totalPnl = 0;
  let sumWinR = 0;
  let sumLossR = 0;
  let winRCount = 0;
  let lossRCount = 0;
  let sumR = 0;
  let rCount = 0;

  for (const t of closed) {
    totalPnl += t.pnl ?? 0;
    if (t.rMultiple !== null) {
      sumR += t.rMultiple;
      rCount++;
    }
    const c = classify(t);
    if (c === 'win') {
      wins++;
      if (t.rMultiple !== null) {
        sumWinR += t.rMultiple;
        winRCount++;
      }
    } else if (c === 'loss') {
      losses++;
      if (t.rMultiple !== null) {
        sumLossR += t.rMultiple;
        lossRCount++;
      }
    } else if (c === 'scratch') {
      scratches++;
    }
  }

  const wlSum = wins + losses;
  const winRate = wlSum > 0 ? wins / wlSum : 0;
  const avgWinR = winRCount > 0 ? sumWinR / winRCount : 0;
  const avgLossR = lossRCount > 0 ? sumLossR / lossRCount : 0;
  const expectancy = winRate * avgWinR + (1 - winRate) * avgLossR;
  const maxDrawdown = computeMaxDrawdown(closed);

  const setups = new Map<string, Trade[]>();
  for (const t of trades) {
    const s = t.plan.setup;
    if (!s) continue;
    let arr = setups.get(s);
    if (!arr) {
      arr = [];
      setups.set(s, arr);
    }
    arr.push(t);
  }
  const bySetup: Record<string, SubStats> = {};
  for (const [k, v] of setups) bySetup[k] = computeSubStats(v);

  const byDirection = {
    long: computeSubStats(trades.filter((t) => t.plan.direction === 'long')),
    short: computeSubStats(trades.filter((t) => t.plan.direction === 'short')),
  };

  return {
    totalTrades: trades.length,
    closedTrades: closed.length,
    openTrades: trades.length - closed.length,
    wins,
    losses,
    scratches,
    winRate,
    avgR: rCount > 0 ? sumR / rCount : 0,
    expectancy,
    totalPnl,
    maxDrawdown,
    bySetup,
    byDirection,
  };
}
